import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

const InlineNodeNameEditor = ({
  value,
  onSave,
  t = (_key, fallback) => fallback,
  disabled = false,
  typographyVariant = 'h6',
  typographySx = {},
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value || ''));

  useEffect(() => {
    if (!editing) {
      setDraft(String(value || ''));
    }
  }, [editing, value]);

  const startEditing = () => {
    if (disabled || typeof onSave !== 'function') return;
    setDraft(String(value || ''));
    setEditing(true);
  };

  const discard = () => {
    setDraft(String(value || ''));
    setEditing(false);
  };

  const save = () => {
    const trimmedName = draft.trim();
    if (!trimmedName) return;
    if (trimmedName !== String(value || '').trim()) {
      onSave(trimmedName);
    }
    setEditing(false);
  };

  if (editing) {
    return (
      <Box sx={{ minWidth: 0, width: '100%', maxWidth: 560 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              save();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              discard();
            }
          }}
          inputProps={{
            maxLength: 255,
            'aria-label': t('generic_connector.datasource_name', 'Node name'),
          }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <IconButton
            size="small"
            onClick={discard}
            aria-label={t('common.cancel', 'Discard name change')}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            onClick={save}
            disabled={!draft.trim()}
            aria-label={t('common.save', 'Save name')}
          >
            <CheckRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={startEditing}
      disabled={disabled || typeof onSave !== 'function'}
      aria-label={t('project_share.edit', 'Edit node name')}
      sx={{
        appearance: 'none',
        border: 0,
        background: 'transparent',
        color: 'inherit',
        p: 0,
        m: 0,
        minWidth: 0,
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: 0.75,
        textAlign: 'left',
        cursor: disabled || typeof onSave !== 'function' ? 'default' : 'pointer',
      }}
    >
      <Typography
        variant={typographyVariant}
        component="span"
        sx={{
          minWidth: 0,
          fontWeight: 600,
          lineHeight: 1.25,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
          ...typographySx,
        }}
      >
        {value}
      </Typography>
      {!disabled && typeof onSave === 'function' && (
        <EditRoundedIcon
          aria-hidden="true"
          sx={{ mt: 0.25, fontSize: 18, flexShrink: 0 }}
        />
      )}
    </Box>
  );
};

export default InlineNodeNameEditor;
